import React from 'react';
import { useStagger } from '../../hooks/useInView';
import TrackedLink from '../global/TrackedLink';
import { CTA_IDS, CTA_LOCATIONS } from '../../lib/analytics.constants';

const ICON_WEB = (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
  </svg>
);
const ICON_APP = (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
  </svg>
);
const ICON_IA = (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
  </svg>
);
const ICON_RS = (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
  </svg>
);
const ICON_PROY = (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
  </svg>
);
const ICON_PART = (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
  </svg>
);

const cell = "bg-white dark:bg-[#111111] border border-gray-100 dark:border-white/[0.07] hover:border-[#11b36d]/40 rounded-2xl transition-colors duration-300";
const label = "text-[10px] font-semibold uppercase tracking-widest text-slate-400 dark:text-slate-500 mb-3";
const iconBadge = "inline-flex items-center justify-center p-2.5 rounded-lg bg-[#11b36d]/10 border border-[#11b36d]/20 text-[#11b36d] mb-4 w-fit";

export default function BentoSection() {
  const { ref: headRef, itemStyle: headStyle } = useStagger({ count: 2, stagger: 70 });
  const { ref: gridRef, itemStyle } = useStagger({ count: 6, stagger: 65 });

  return (
    <section className="py-28 px-4 bg-white dark:bg-[#0d0d0d]">
      <div className="max-w-6xl mx-auto">

        {/* Header */}
        <div ref={headRef} className="mb-12">
          <p style={headStyle(0)} className="w-fit text-[10px] font-medium uppercase tracking-[0.2em] text-[#11b36d] bg-[#11b36d]/10 border border-[#11b36d]/20 rounded-full px-3 py-1 mb-5">Qué hacemos · Cómo trabajamos</p>
          <h2 style={headStyle(1)} className="text-3xl font-thin tracking-tighter sm:text-4xl text-[#0b2b40] dark:text-white">
            Todo lo que <span className="text-gradient text-short">necesitás</span>
          </h2>
        </div>

        {/* Bento grid — 4 cols desktop, 2 cols tablet, 1 mobile */}
        <div ref={gridRef} className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3 auto-rows-[minmax(180px,auto)]">

          {/* ── ROW 1 ────────────────────────────────── */}

          {/* Webs — cuadrado */}
          <TrackedLink to="/capacidades" ctaId={CTA_IDS.BENTO_WEBS} ctaLocation={CTA_LOCATIONS.BENTO} style={itemStyle(0)} className={`${cell} p-7 flex flex-col`}>
            <div className={iconBadge}>{ICON_WEB}</div>
            <p className={label}>Servicio</p>
            <h3 className="text-lg font-medium text-[#0b2b40] dark:text-white mb-2">Webs</h3>
            <p className="text-sm text-slate-500 dark:text-slate-400 leading-relaxed flex-grow">
              Sitios y apps web rápidos, escalables y pensados para convertir.
            </p>
          </TrackedLink>

          {/* Apps — rectángulo ancho (2 cols) */}
          <TrackedLink to="/capacidades" ctaId={CTA_IDS.BENTO_APPS} ctaLocation={CTA_LOCATIONS.BENTO} style={itemStyle(1)} className={`${cell} md:col-span-2 p-7 flex flex-col`}>
            <div className={iconBadge}>{ICON_APP}</div>
            <p className={label}>Servicio</p>
            <h3 className="text-lg font-medium text-[#0b2b40] dark:text-white mb-2">Apps móviles</h3>
            <p className="text-sm text-slate-500 dark:text-slate-400 leading-relaxed mb-4 flex-grow">
              iOS y Android nativos, rápidos y conectados a tu negocio. Lanzá en semanas, no meses.
            </p>
          </TrackedLink>

          {/* IA — cuadrado destacado */}
          <TrackedLink to="/ia" ctaId={CTA_IDS.BENTO_IA} ctaLocation={CTA_LOCATIONS.BENTO} style={itemStyle(2)} className="sm:col-span-2 md:col-span-1 order-first md:order-none bg-[#f4fdf8] dark:bg-[#0b1a10] border border-[#11b36d]/50 hover:border-[#11b36d]/80 rounded-2xl shadow-[0_0_28px_rgba(17,179,109,0.07)] dark:shadow-[0_0_32px_rgba(17,179,109,0.13)] transition-colors duration-300 p-7 flex flex-col relative overflow-hidden">
            <div className="absolute top-4 right-4">
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-[#11b36d]/10 text-[#11b36d] border border-[#11b36d]/25">Nuevo</span>
            </div>
            <div className="inline-flex items-center justify-center p-2.5 rounded-lg bg-[#11b36d]/15 border border-[#11b36d]/30 text-[#11b36d] mb-4 w-fit">
              {ICON_IA}
            </div>
            <p className={label}>Servicio</p>
            <h3 className="text-lg font-medium text-[#0b2b40] dark:text-white mb-2">Inteligencia Artificial</h3>
            <p className="text-sm text-slate-500 dark:text-slate-400 leading-relaxed flex-grow">
              Asistentes reales, documentos automáticos, leads que no se pierden.
            </p>
            <div className="mt-4 inline-flex items-center gap-1 text-xs font-semibold text-[#11b36d] hover:underline transition-colors">
              Saber más
              <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </div>
          </TrackedLink>

          {/* ── ROW 2 · Modelos: Proyecto → Partnership → Revenue Share ── */}

          {/* Proyecto — cuadrado */}
          <TrackedLink to="/modelos#proyecto" ctaId={CTA_IDS.BENTO_PROYECTO} ctaLocation={CTA_LOCATIONS.BENTO} modelName="proyecto" style={itemStyle(3)} className="group">
            <div className={`${cell} p-7 flex flex-col h-full`}>
              <div className={iconBadge}>{ICON_PROY}</div>
              <p className={label}>Modelo</p>
              <h3 className="text-lg font-medium text-[#0b2b40] dark:text-white mb-2">Proyecto</h3>
              <p className="text-sm text-slate-500 dark:text-slate-400 leading-relaxed flex-grow">
                Alcance y precio definidos, pago por etapas. Empezamos, entregamos, queda tuyo.
              </p>
            </div>
          </TrackedLink>

          {/* Partnership — cuadrado */}
          <TrackedLink to="/modelos#partnership" ctaId={CTA_IDS.BENTO_PARTNERSHIP} ctaLocation={CTA_LOCATIONS.BENTO} modelName="partnership" style={itemStyle(4)} className="group">
            <div className={`${cell} p-7 flex flex-col h-full`}>
              <div className={iconBadge}>{ICON_PART}</div>
              <p className={label}>Modelo</p>
              <h3 className="text-lg font-medium text-[#0b2b40] dark:text-white mb-2">Partnership</h3>
              <p className="text-sm text-slate-500 dark:text-slate-400 leading-relaxed flex-grow">
                Tu equipo tech continuo por un fee mensual fijo. La tecnología evoluciona con vos, sin participación en ingresos.
              </p>
            </div>
          </TrackedLink>

          {/* Revenue Share — rectángulo ancho (2 cols), destacado */}
          <TrackedLink to="/modelos#revenue-share" ctaId={CTA_IDS.BENTO_REVENUE_SHARE} ctaLocation={CTA_LOCATIONS.BENTO} modelName="revenue_share" style={itemStyle(5)} className="sm:col-span-2 md:col-span-2 order-last md:order-none bg-[#f4fdf8] dark:bg-[#0b1a10] border border-[#11b36d]/50 hover:border-[#11b36d]/80 rounded-2xl shadow-[0_0_28px_rgba(17,179,109,0.07)] dark:shadow-[0_0_32px_rgba(17,179,109,0.13)] transition-colors duration-300 p-7 flex flex-col">
            <div className="inline-flex items-center justify-center p-2.5 rounded-lg bg-[#11b36d]/15 border border-[#11b36d]/30 text-[#11b36d] mb-4 w-fit">
              {ICON_RS}
            </div>
            <p className={label}>Modelo</p>
            <h3 className="text-lg font-medium text-[#0b2b40] dark:text-white mb-2">Revenue Share</h3>
            <p className="text-sm text-slate-500 dark:text-slate-400 leading-relaxed flex-grow">
              Crecemos cuando vos crecés. Pagás un fee base reducido más un porcentaje de tus ingresos, alineamos incentivos desde el día uno.
            </p>
            <div className="flex flex-wrap gap-2 mt-4">
              {['1% a 18% de ingresos', 'Métricas compartidas', 'Cap negociable', 'Revisiones trimestrales'].map(t => (
                <span key={t} className="text-xs px-2.5 py-1 rounded-full bg-[#11b36d]/10 text-[#11b36d] border border-[#11b36d]/25">{t}</span>
              ))}
            </div>
          </TrackedLink>

        </div>

        {/* CTAs */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mt-10">
          <TrackedLink to="/capacidades" ctaId={CTA_IDS.BENTO_CAPACIDADES} ctaLocation={CTA_LOCATIONS.BENTO}>
            <button className="group inline-flex items-center gap-3 bg-[#11b36d] text-white pl-6 pr-2 py-2 rounded-full font-semibold text-sm hover:bg-[#0ea060] shadow-[0_2px_8px_rgba(17,179,109,0.22),inset_0_1px_0_rgba(255,255,255,0.12)] hover:shadow-[0_4px_14px_rgba(17,179,109,0.32),inset_0_1px_0_rgba(255,255,255,0.12)] active:scale-[0.97] transition-all duration-500 ease-[cubic-bezier(0.32,0.72,0,1)]">
              Ver todas las capacidades
              <span className="w-8 h-8 rounded-full bg-black/10 flex items-center justify-center shrink-0 group-hover:translate-x-0.5 group-hover:-translate-y-px transition-transform duration-300 ease-[cubic-bezier(0.32,0.72,0,1)]">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                </svg>
              </span>
            </button>
          </TrackedLink>
          <TrackedLink to="/modelos" ctaId={CTA_IDS.BENTO_MODELOS} ctaLocation={CTA_LOCATIONS.BENTO}>
            <button className="group inline-flex items-center gap-3 border border-gray-200 dark:border-white/[0.12] text-slate-600 dark:text-slate-300 pl-6 pr-2 py-2 rounded-full font-semibold text-sm hover:border-[#11b36d]/50 hover:text-[#11b36d] transition-all duration-500 ease-[cubic-bezier(0.32,0.72,0,1)]">
              Ver modelos de trabajo
              <span className="w-8 h-8 rounded-full bg-gray-100 dark:bg-white/[0.06] flex items-center justify-center shrink-0 group-hover:translate-x-0.5 group-hover:-translate-y-px group-hover:bg-[#11b36d]/10 transition-all duration-300 ease-[cubic-bezier(0.32,0.72,0,1)]">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                </svg>
              </span>
            </button>
          </TrackedLink>
        </div>

      </div>
    </section>
  );
}
